import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useNavigate, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import AppleOAuth from '../../components/auth/AppleOAuth';
import GoogleOAuth from '../../components/auth/GoogleOAuth';
import { Button, Input, Card } from '../../components/ui';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import Footer from '../../components/layout/Footer';
import { getMemberApplicationCampIdentifier, getOnboardingRedirectPath, getSafeRedirectPath } from '../../utils/authRedirects';
import {
  PHONE_COUNTRY_CODE_OPTIONS,
  isValidPhoneCountryCode,
  normalizePhoneCountryCode
} from '../../utils/phone';

type FormData = {
  firstName: string;
  lastName: string;
  phoneCountryCode: string;
  phoneNumber: string;
  email: string;
  password: string;
  confirmPassword: string;
};

const schema: yup.ObjectSchema<FormData> = yup
  .object({
    firstName: yup.string().required('First name is required'),
    lastName: yup.string().required('Last name is required'),
    phoneCountryCode: yup
      .string()
      .required('Country code is required')
      .test('phone-country-code', 'Enter a valid country code, like +1', (value) => isValidPhoneCountryCode(value)),
    phoneNumber: yup
      .string()
      .required('Phone number is required')
      .test('phone-number-digits', 'Phone number must contain 7 to 20 digits', (value) => {
        const digitCount = String(value || '').replace(/\D/g, '').length;
        return digitCount >= 7 && digitCount <= 20;
      }),
    email: yup.string().email('Invalid email').required('Email is required'),
    password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], 'Passwords must match')
      .required('Confirm password is required'),
  })
  .required();

const Register: React.FC = () => {
  const { register: registerUser, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordMatch, setPasswordMatch] = useState(true);
  const [oauthLoading, setOauthLoading] = useState(false);
  
  // Capture invitation context from URL
  const inviteToken = searchParams.get('invite_token') || searchParams.get('invite');
  const campSlug = searchParams.get('camp');
  const isShiftsOnlyInviteFromQuery = searchParams.get('shifts_only') === '1';
  const redirectPath = searchParams.get('redirect');
  const safeRedirect = getSafeRedirectPath(redirectPath);
  const applicationCampIdentifier = getMemberApplicationCampIdentifier(safeRedirect);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      phoneCountryCode: '+1'
    },
  });

  // Validate and store invitation context when token is present.
  // Invalid/expired tokens fall back to normal signup.
  useEffect(() => {
    let isActive = true;

    const validateInvite = async () => {
      if (!inviteToken) return;

      try {
        const response = await api.get(`/invites/validate/${inviteToken}`);
        const resolvedCampSlug = response.campSlug || campSlug;

        if (!resolvedCampSlug) {
          throw new Error('Invalid invitation link');
        }

        if (!isActive) return;

        localStorage.setItem(
          'pendingInvite',
          JSON.stringify({
            token: inviteToken,
            campSlug: resolvedCampSlug,
            mode: response?.isShiftsOnlyInvite || isShiftsOnlyInviteFromQuery ? 'shifts_only' : 'standard'
          })
        );
        setError('');
      } catch (err: any) {
        if (!isActive) return;
        localStorage.removeItem('pendingInvite');
        setError(err?.response?.data?.message || 'Invitation link is invalid or expired. Please sign up normally.');
        navigate('/register', { replace: true });
      }
    };

    validateInvite();
    return () => {
      isActive = false;
    };
  }, [inviteToken, campSlug, navigate, isShiftsOnlyInviteFromQuery]);

  // Redirect authenticated users away from register page
  useEffect(() => {
    console.log('🔍 [Register useEffect] authLoading:', authLoading, 'user:', user);
    
    // Wait for auth to finish loading before checking user status
    if (authLoading) {
      console.log('🔍 [Register] Auth still loading, waiting...');
      return;
    }
    
    if (user) {
      console.log('🔍 [Register] User already authenticated, redirecting...');

      // If user arrived via invite while already authenticated, skip onboarding
      // and continue directly to the camp application flow.
      const pendingInvite = localStorage.getItem('pendingInvite');
      if (pendingInvite) {
        try {
          const { campSlug, token, mode } = JSON.parse(pendingInvite);
          if (campSlug && token) {
            if (mode === 'shifts_only') {
              navigate(`/onboarding/shifts-only?invite_token=${token}`, { replace: true });
            } else {
              navigate(`/apply?invite_token=${token}`, { replace: true });
            }
            return;
          }
        } catch (err) {
          localStorage.removeItem('pendingInvite');
        }
      }
      
      // Check if user needs onboarding
      if (user.role === 'unassigned' || !user.role) {
        console.log('✅ [Register] Redirecting to onboarding...');
        navigate(getOnboardingRedirectPath(safeRedirect), { replace: true });
        return;
      }
      
      // Redirect to dashboard
      console.log('✅ [Register] Redirecting to dashboard...');
      navigate(safeRedirect, { replace: true });
    }
  }, [user, authLoading, navigate, safeRedirect]);

  const password = watch('password');
  const confirmPassword = watch('confirmPassword');

  // Check password match in real-time
  React.useEffect(() => {
    if (confirmPassword && password !== confirmPassword) {
      setPasswordMatch(false);
    } else if (confirmPassword && password === confirmPassword && password.length >= 6) {
      setPasswordMatch(true);
    } else {
      setPasswordMatch(true);
    }
  }, [password, confirmPassword]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError('');

    try {
      // Check password match first
      if (data.password !== data.confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      const { confirmPassword, ...registerData } = data;
      const pendingInvite = localStorage.getItem('pendingInvite');
      let inviteTokenForSignup: string | undefined;
      if (pendingInvite) {
        try {
          const { token } = JSON.parse(pendingInvite);
          inviteTokenForSignup = token;
        } catch (err) {
          localStorage.removeItem('pendingInvite');
        }
      }
      
      // Always create personal account - role will be selected during onboarding
      const userData = {
        ...registerData,
        phoneCountryCode: normalizePhoneCountryCode(registerData.phoneCountryCode),
        phoneNumber: registerData.phoneNumber.trim(),
        accountType: 'personal' as const,
        inviteToken: inviteTokenForSignup,
        ...(applicationCampIdentifier && !inviteTokenForSignup
          ? {
              signupIntent: 'member_application' as const,
              applicationCampIdentifier
            }
          : {})
      };
      
      const result = await registerUser(userData);
      
      // Small delay to ensure auth context is fully updated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Check for pending invitation
      const pendingInviteAfterSignup = localStorage.getItem('pendingInvite');
      if (pendingInviteAfterSignup) {
        try {
          const { campSlug, token, mode } = JSON.parse(pendingInviteAfterSignup);
          console.log('🎟️ [Register] Redirecting with invite:', { campSlug, token, mode });
          if (mode === 'shifts_only') {
            navigate(`/onboarding/shifts-only?invite_token=${token}`, { replace: true });
          } else {
            navigate(`/apply?invite_token=${token}`, { replace: true });
          }
          return;
        } catch (err) {
          console.error('❌ [Register] Error parsing pending invite:', err);
          localStorage.removeItem('pendingInvite');
        }
      }
      
      // Check if user needs onboarding
      if (result.needsOnboarding && !applicationCampIdentifier) {
        navigate(getOnboardingRedirectPath(safeRedirect), { replace: true });
        return;
      }
      
      // Fallback redirect (should not happen with new users)
      navigate(safeRedirect);
    } catch (err: any) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSuccess = (oauthData: any) => {
    console.log('🔍 [Register] OAuth success callback triggered with data:', oauthData);
    
    // Extract user, token, and isNewUser flag from OAuth response
    const user = oauthData.user || oauthData;
    const token = oauthData.token;
    const isNewUser = oauthData.isNewUser; // Backend tells us if this is a new user
    
    console.log('🔍 [Register] Extracted user:', user);
    console.log('🔍 [Register] Token present:', !!token);
    console.log('🔍 [Register] isNewUser flag from backend:', isNewUser);
    console.log('🔍 [Register] User accountType:', user.accountType);
    
    setOauthLoading(false);
    setError('');
    
    // OAuth registration must use isNewUser flag (see Login.tsx for full explanation)
    // Register page: We expect mostly new users, but existing users can also click "Sign up with Google"
    
    // Small delay to ensure localStorage write completes
    setTimeout(() => {
      console.log('🔄 [Register] Reloading to update AuthContext...');

      const pendingInvite = localStorage.getItem('pendingInvite');
      if (pendingInvite) {
        try {
          const { campSlug, token, mode } = JSON.parse(pendingInvite);
          if (campSlug && token) {
            window.location.href = mode === 'shifts_only'
              ? `/onboarding/shifts-only?invite_token=${token}`
              : `/apply?invite_token=${token}`;
            return;
          }
        } catch (err) {
          localStorage.removeItem('pendingInvite');
        }
      }
      
      // TRUST THE BACKEND: Use isNewUser flag
      if (isNewUser && applicationCampIdentifier) {
        console.log('✅ [Register] New member application user, redirecting to camp application...');
        window.location.href = safeRedirect;
      } else if (isNewUser) {
        // Brand new user - go to onboarding
        console.log('✅ [Register] New user, redirecting to onboarding...');
        window.location.href = getOnboardingRedirectPath(safeRedirect);
      } else {
        // Existing user who clicked "Sign up" instead of "Sign in" - go to dashboard
        console.log('✅ [Register] Existing user, redirecting to dashboard...');
        window.location.href = safeRedirect;
      }
    }, 100); // 100ms delay to ensure localStorage write completes
  };

  const handleOAuthError = (error: string) => {
    setOauthLoading(false);
    setError(error);
  };

  return (
    <div className="min-h-screen bg-custom-bg flex flex-col">
      <div className="flex-1 flex items-center justify-center py-8 px-4">
        <Card className="w-full max-w-md p-6 sm:p-8">
          <div className="text-center mb-8">
          {/* Logo */}
          <div className="mb-4">
            <img 
              src="/logo-small.svg" 
              alt="G8Road Logo" 
              className="h-12 mx-auto"
            />
          </div>
          
          <h1 className="text-h1 font-lato-bold text-custom-text mb-2">
            Join the Community
          </h1>
          <p className="text-body text-custom-text-secondary">
            Create your G8Road CRM account
          </p>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          {/* OAuth Section */}
          <div className="mb-6">
            <p className="text-body text-custom-text-secondary mb-4 text-center">
              Or sign up with
            </p>
            <div className="flex flex-col gap-3">
              <GoogleOAuth
                onSuccess={handleOAuthSuccess}
                onError={handleOAuthError}
                disabled={oauthLoading || loading}
                mode="signup"
                inviteToken={inviteToken}
                signupIntent={applicationCampIdentifier && !inviteToken ? 'member_application' : null}
                applicationCampIdentifier={applicationCampIdentifier}
              />
              <AppleOAuth
                onSuccess={handleOAuthSuccess}
                onError={handleOAuthError}
                disabled={oauthLoading || loading}
                inviteToken={inviteToken}
                signupIntent={applicationCampIdentifier && !inviteToken ? 'member_application' : null}
                applicationCampIdentifier={applicationCampIdentifier}
              />
            </div>
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-custom-text-secondary">Or continue with email</span>
            </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Personal Information */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Input
                  {...register('firstName')}
                  label="First Name"
                  error={errors.firstName?.message}
                  className="w-full"
                />
                <p className="text-gray-500 text-xs mt-1 ml-1">or your camp's name</p>
              </div>
              <Input
                {...register('lastName')}
                label="Last Name"
                error={errors.lastName?.message}
                className="w-full"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] gap-4">
              <Input
                {...register('phoneCountryCode')}
                label="Country Code *"
                type="tel"
                list="register-phone-country-codes"
                error={errors.phoneCountryCode?.message}
                placeholder="+1"
                className="w-full"
              />
              <Input
                {...register('phoneNumber')}
                label="Phone Number *"
                type="tel"
                error={errors.phoneNumber?.message}
                placeholder="555 123 4567"
                className="w-full"
              />
              <datalist id="register-phone-country-codes">
                {PHONE_COUNTRY_CODE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </datalist>
            </div>

            <Input
              {...register('email')}
              label="Email Address"
              type="email"
              error={errors.email?.message}
              className="w-full"
            />

            <div className="relative">
              <Input
                {...register('password')}
                label="Password"
                type={showPassword ? 'text' : 'password'}
                error={errors.password?.message}
                className="w-full pr-10"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className="h-5 w-5 text-custom-text-secondary" />
                ) : (
                  <Eye className="h-5 w-5 text-custom-text-secondary" />
                )}
              </button>
            </div>

            <div className="relative">
              <Input
                {...register('confirmPassword')}
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                error={errors.confirmPassword?.message || (!passwordMatch ? 'Passwords do not match' : '')}
                className="w-full pr-10"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5 text-custom-text-secondary" />
                ) : (
                  <Eye className="h-5 w-5 text-custom-text-secondary" />
                )}
              </button>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={loading}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin h-5 w-5 mr-2" />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </Button>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-custom-text-secondary">OR</span>
              </div>
            </div>

            <div className="text-center">
              <p className="text-body text-custom-text-secondary mb-4">
                Already have an account?
              </p>
              <RouterLink
                to={safeRedirect !== '/dashboard'
                  ? `/login?redirect=${encodeURIComponent(safeRedirect)}`
                  : '/login'}
                className="block"
              >
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full"
                >
                  Sign In
                </Button>
              </RouterLink>
            </div>
          </form>
        </Card>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Register;
